import { useEffect, useState } from 'react';

import { set } from 'lodash';
import { useApolloClient } from 'react-apollo';
import { useHistory, useParams } from 'react-router';

import { Field } from '../Components-redesign/Form';
import { InputTypes } from '../Components-redesign/Form/fieldTypes';
import { getLangText } from '../utils/langText';

import { useSelector } from '.';

const getFormData = async (client, query, id, companyId) => {
  const { data } = await client.query({
    query: query,
    variables: {
      id: id.replace('group-', ''),
    },
    fetchPolicy: 'network-only',
  });

  if (!data?.node?.company?.id || data?.node?.company?.id == companyId) {
    return data;
  } else return;
};

const mapDataToFormState = (data: any, formState: Field[]) =>
  formState.reduce((accum, field: Field) => {
    const splittedId = field.id.split('.');
    let value;

    // If field has ignoreServerData flag, use the field's value instead of server data
    if (field.ignoreServerData) {
      value = field.value;
    } else if (splittedId.length > 1) {
      value = data.node[splittedId[0]][splittedId[1]] || '';
    } else if (field.id == 'categories' && data.node[field.id][0] && data.node[field.id][0].id) {
      // handles category
      value = data.node[field.id][0].id;
    } else if (field.type == 'SELECT') {
      value = data.node[field.id]?.id ?? data.node[field.id] ?? '';
    } else {
      if (data.node[field.id] !== undefined) {
        value = data.node[field.id];
      } else {
        if (typeof field.value === 'boolean') {
          value = false;
        } else {
          value = field.value || '';
        }
      }
    }
    if (field.type == InputTypes.PRICE) {
      value = data.node[field.id] / 100;
    }
    return [...accum, { ...field, value, hasPrevValue: Boolean(value) }];
  }, []);

const getMutationData = (
  formFields,
  idToUpdate,
  companyId,
  withDataFormat = true,
  defaultLanguage,
) => {
  let base = {};

  Object.values(formFields).map((field: Field) => {
    if (!field || field.type == InputTypes.TITLEBAR || field.id.includes('hiddenLabel')) return;

    //If the field has a customMutation (different way to handle the value)
    if (field.customMutation) {
      base = { ...base, [field.id]: field.customMutation(field.value, field.hasPrevValue) };

      if (field.additionalFieldToUpdate) {
        base[field.additionalFieldToUpdate] = getLangText(field.value ?? '', defaultLanguage);
      }
    }
    // If the field is INT or VAT parse it to an INT
    else if (field.type == InputTypes.INT || field.type == InputTypes.SELECTVAT) {
      base = { ...base, [field.id]: parseInt(field.value) };
    }
    //If the field is Price convert it to the correct price
    else if (field.type == InputTypes.PRICE) {
      const formattedPrice = field.value.toString().replace(' ', '').replace(',', '.');
      const price = parseFloat(formattedPrice) * 100;
      base = { ...base, [field.id]: Math.round(price) };
    }
    // If the field is a language field use create in mutation
    else if (field.type == InputTypes.TEXTLANG || field.type == InputTypes.TEXTAREALANG) {
      if (!field.value) return;
      delete field.value['__typename'];

      if (idToUpdate) {
        delete field.value.id;
      }

      base = { ...base, [field.id]: { create: field.value } };
      if (field.additionalFieldToUpdate) {
        base[field.additionalFieldToUpdate] = getLangText(field.value ?? '', defaultLanguage);
      }
    }
    // Use field value to field id
    else if (field.type == InputTypes.SELECT && field.value) {
      base = { ...base, [field.id]: field.value };
    }
    // Connect/Disconnect image fields
    else if (field.type == InputTypes.IMAGESELECT && field.id == 'image') {
      const imageValue = field.value?.id ?? field.value;
      if (!field.hasPrevValue && !field.value) {
        return;
      } else if (field.hasPrevValue && imageValue.length < 1) {
        base = { ...base, [field.id]: { disconnect: true } };
      } else if (imageValue.length > 0) {
        base = { ...base, [field.id]: { connect: { id: field.value.id ?? field.value } } };
      }
    }
    // Use field value to field id
    else {
      base = { ...base, [field.id]: field.value };
    }
  });

  const groupedObj = {};
  Object.entries(base).forEach(([key, value]) => set(groupedObj, key, value));

  if (idToUpdate) {
    return {
      id: idToUpdate.replace('group-', ''),
      data: {
        ...groupedObj,
      },
    };
  } else if (withDataFormat) {
    return {
      data: {
        ...groupedObj,
        company: { connect: { id: companyId } },
      },
    };
  } else {
    return {
      ...groupedObj,
      companyId,
    };
  }
};

const useFormData = (initialId, initialFields, query?) => {
  const companyId = useSelector((state) => state.company);
  const { path } = useParams<{ path: string; id?: string }>();
  const history = useHistory();
  const [formState, setFormState] = useState(initialFields);
  const client = useApolloClient();
  const [, basePath] = history.location.pathname.split('/');

  useEffect(() => {
    if (initialId && query) {
      getFormData(client, query, initialId, companyId).then((data) => {
        if (!data?.node) {
          if (path) {
            history.push('/' + basePath + '/' + path);
          } else {
            history.push('/' + basePath);
          }
        } else {
          const updatedFields = mapDataToFormState(data, initialFields.filter(Boolean));
          setFormState(updatedFields);
        }
      });
    }
  }, [initialId]);

  return [formState, getMutationData];
};

export default useFormData;
